import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, ArrowUpRight, ShoppingBag, Check, Sparkles } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import useReveal from '../hooks/useReveal';
import Seo from '../components/Seo';
import OrderDirectBanner from '../components/OrderDirectBanner';
import CompetitionSpotlight from '../components/CompetitionSpotlight';
import DopamineSpotlight from '../components/DopamineSpotlight';
import { IMAGES, STATS, SERVICE_ICONS, PRODUCT_IMAGES, WHY_ICONS } from '../mock/mock';
import { SHOP_PRODUCTS } from '../mock/shop';

const Kicker = ({ children }) => (
  <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold mb-4">
    <span className="w-8 h-px bg-gold" />{children}
  </span>
);

const Home = () => {
  const { t, lang } = useLang();
  const { add } = useCart();
  const [addedId, setAddedId] = useState(null);
  useReveal(lang);
  const services = t('services.items');
  const products = t('products.categories');
  const why = t('home.whyItems');
  const featured = SHOP_PRODUCTS.filter((p) => p.badge === 'Best Seller' || p.badge === 'New').slice(0, 4);
  const addFeatured = (p) => { add(p, 1); setAddedId(p.id); setTimeout(() => setAddedId(null), 1200); };

  return (
    <div className="overflow-hidden">
      <Seo title="OEM & Private Label Luxury Cosmetics Manufacturer"
        path="/"
        description="ARGE Cosmetics is a GMP / ISO 22716-certified OEM, private-label and contract manufacturer of ultra-luxury skincare, haircare and body care — produced in Türkiye for brands worldwide, plus the Wellmax & Maxwell online store." />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-28 pb-16 bg-gradient-to-b from-cream to-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center w-full">
          <div className="animate-fade-up">
            <Kicker>{t('hero.badge')}</Kicker>
            <h1 className="font-serif text-5xl md:text-6xl xl:text-7xl leading-[1.05] text-espresso font-medium">
              {t('hero.title')}
            </h1>
            <p className="mt-7 text-lg text-mocha leading-relaxed max-w-xl">{t('hero.subtitle')}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="group gold-sheen inline-flex items-center gap-3 px-8 py-4 bg-espresso text-ivory text-xs tracking-[0.18em] uppercase hover:bg-gold transition-colors duration-400">
                {t('hero.cta1')}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
              </Link>
              <Link to="/services" className="inline-flex items-center gap-3 px-8 py-4 border border-espresso/30 text-espresso text-xs tracking-[0.18em] uppercase hover:border-gold hover:text-gold transition-colors duration-400">
                {t('hero.cta2')}
              </Link>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: '0.15s' }}>
            <div className="img-zoom relative aspect-[4/5] w-full max-w-md mx-auto shadow-[0_40px_80px_-30px_rgba(43,36,32,0.4)]">
              <img src={IMAGES.heroBottle} alt="Premium cosmetics" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 rtl:-left-auto rtl:-right-6 bg-ivory px-7 py-5 shadow-xl border-t-2 border-gold animate-floaty">
              <p className="font-serif text-4xl text-espresso">15<span className="text-gold">+</span></p>
              <p className="text-[0.65rem] tracking-[0.2em] uppercase text-mocha mt-1">{t('stats.expertise')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPETITION SPOTLIGHT */}
      <CompetitionSpotlight />

      <DopamineSpotlight />

      {/* STATS */}
      <section className="bg-espresso text-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.key} className="text-center reveal">
              <p className="font-serif text-5xl md:text-6xl text-gold-light">{s.value}</p>
              <p className="text-[0.68rem] tracking-[0.22em] uppercase text-ivory/70 mt-3">{t(`stats.${s.key}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative reveal">
            <div className="img-zoom aspect-[5/6] w-full shadow-[0_30px_70px_-30px_rgba(43,36,32,0.35)]">
              <img src={IMAGES.lab} alt="Cosmetics laboratory" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-6 -right-6 rtl:-right-auto rtl:-left-6 w-28 h-28 border border-gold/40 -z-0 hidden md:block" />
          </div>
          <div className="reveal">
            <Kicker>{t('home.introKicker')}</Kicker>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">{t('home.introTitle')}</h2>
            <p className="mt-6 text-mocha leading-relaxed">{t('home.introText')}</p>
            <p className="mt-4 text-mocha leading-relaxed">{t('home.introText2')}</p>
            <Link to="/rnd" className="group mt-8 inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase text-espresso border-b border-gold pb-1 hover:text-gold transition-colors">
              {t('nav.rnd')}<ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto reveal">
            <Kicker>{t('home.servicesKicker')}</Kicker>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso">{t('home.servicesTitle')}</h2>
            <p className="mt-5 text-mocha">{t('home.servicesSubtitle')}</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((s, i) => {
              const Icon = Icons[SERVICE_ICONS[i]] || Icons.Sparkles;
              return (
                <div key={i} className="reveal hover-lift bg-ivory p-8 border border-transparent hover:border-gold-soft" style={{ transitionDelay: `${(i % 3) * 60}ms` }}>
                  <div className="w-12 h-12 flex items-center justify-center bg-cream text-gold mb-5"><Icon className="w-6 h-6" /></div>
                  <h3 className="font-serif text-2xl text-espresso mb-3">{s.title}</h3>
                  <p className="text-sm text-mocha leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12 reveal">
            <Link to="/services" className="inline-flex items-center gap-3 px-8 py-4 border border-espresso/30 text-espresso text-xs tracking-[0.18em] uppercase hover:border-gold hover:text-gold transition-colors">
              {t('nav.services')}<ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      {/* SHOP / FEATURED PRODUCTS */}
      <section className="py-24 md:py-32 satin-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 reveal">
            <div className="max-w-xl">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold"><span className="w-8 h-px bg-gold" />Shop Our Brands</span>
                <span data-testid="home-shop-coming-soon-badge" className="inline-flex items-center gap-1.5 text-[0.6rem] tracking-[0.18em] uppercase bg-gold text-espresso px-2.5 py-1 rounded-full"><Sparkles className="w-3 h-3" /> Coming Soon!</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">Wellmax & Maxwell — Buy Direct</h2>
              <p className="mt-4 text-mocha">Beyond manufacturing for others, we sell our own salon-quality hair, skin and body care. Shop authentic ARGE brands, delivered across the UK.</p>
            </div>
            <div className="shrink-0 flex flex-col items-start md:items-end gap-2">
              <Link to="/shop" className="group inline-flex items-center gap-3 px-8 py-4 bg-espresso text-ivory text-xs tracking-[0.18em] uppercase hover:bg-gold transition-colors">
                Visit the Store<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
              </Link>
              <span data-testid="home-shop-coming-soon-note" className="text-[0.72rem] tracking-wide text-gold">Online shopping coming soon to the UK &amp; EU</span>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => (
              <div key={p.id} className="reveal group bg-ivory border border-transparent hover:border-gold-soft flex flex-col hover-lift">
                <Link to={`/shop/${p.id}`} className="relative block bg-white p-6 aspect-square overflow-hidden">
                  {p.badge && <span className="absolute top-3 left-3 z-10 text-[0.6rem] tracking-[0.12em] uppercase bg-gold text-espresso px-2 py-0.5">{p.badge}</span>}
                  <img src={p.image} alt={p.name} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[0.62rem] tracking-[0.18em] uppercase text-gold">{p.brand}</p>
                  <Link to={`/shop/${p.id}`} className="text-sm text-espresso mt-1 leading-snug hover:text-gold transition-colors line-clamp-2 min-h-[2.5rem]">{p.name}</Link>
                  <span className="font-serif text-xl text-espresso mt-2">£{p.price.toFixed(2)}</span>
                  <button onClick={() => addFeatured(p)} className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-espresso text-ivory text-[0.68rem] tracking-[0.16em] uppercase hover:bg-gold transition-colors">
                    {addedId === p.id ? <><Check className="w-4 h-4" /> Added</> : <><ShoppingBag className="w-4 h-4" /> Add to Bag</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORDER DIRECT & SAVE */}
      <OrderDirectBanner compact />

      {/* PRODUCTS PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto reveal">
            <Kicker>{t('home.productsKicker')}</Kicker>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso">{t('home.productsTitle')}</h2>
            <p className="mt-5 text-mocha">{t('home.productsSubtitle')}</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.slice(0, 3).map((p, i) => (
              <Link to="/products" key={i} className="group reveal relative img-zoom aspect-[3/4] block bg-cream" style={{ transitionDelay: `${i * 80}ms` }}>
                <img src={PRODUCT_IMAGES[i]} alt={p.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/85 via-espresso/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <h3 className="font-serif text-3xl text-ivory">{p.title}</h3>
                  <p className="text-ivory/75 text-sm mt-2 line-clamp-2">{p.desc}</p>
                  <span className="inline-flex items-center gap-2 mt-4 text-gold-light text-xs tracking-[0.18em] uppercase opacity-0 group-hover:opacity-100 transition-opacity">{t('nav.products')}<ArrowUpRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto reveal">
            <Kicker>{t('home.whyKicker')}</Kicker>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso">{t('home.whyTitle')}</h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {why.map((w, i) => {
              const Icon = Icons[WHY_ICONS[i]] || Icons.Check;
              return (
                <div key={i} className="reveal flex gap-5" style={{ transitionDelay: `${(i % 3) * 60}ms` }}>
                  <div className="shrink-0 w-11 h-11 flex items-center justify-center border border-gold/40 text-gold"><Icon className="w-5 h-5" /></div>
                  <div>
                    <h3 className="font-serif text-xl text-espresso mb-2">{w.title}</h3>
                    <p className="text-sm text-mocha leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 bg-espresso overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={IMAGES.serumSplash} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center reveal">
          <h2 className="font-serif text-4xl md:text-5xl text-ivory leading-tight">{t('home.ctaTitle')}</h2>
          <p className="mt-6 text-ivory/75 leading-relaxed">{t('home.ctaText')}</p>
          <Link to="/contact" className="group gold-sheen mt-9 inline-flex items-center gap-3 px-10 py-4 bg-gold text-espresso text-xs tracking-[0.2em] uppercase hover:bg-gold-light transition-colors">
            {t('home.ctaButton')}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
